import { Box } from '@amzn/awsui-components-core';
import {
  TiptapEditor,
  TiptapEditorView,
  Bold,
  Italic,
  Underline,
  Table,
  BulletList,
  Code,
  Newline,
  OrderedList,
  Redo,
  TextAlignCenter,
  TextAlignLeft,
  TextAlignRight,
  Undo,
  Image,
  Link,
  TiptapOutput,
  BlockFormatGroup,
  ClearFormatting,
  SearchAndReplace,
  TiptapEditorHTMLView,
  CodeView,
  TiptapEditorCommentView,
  Youtube,
} from '@amzn/awscat-editor';
import React from 'react';
import { IMplServiceClient } from '../api/IMplServiceClient';
import { MplServiceClientFactory } from '../api/MplServiceClientFactory';
import { isURL } from 'class-validator';
import { ContentSectionKey } from '../model/ContentSchemaSection';
import { TiptapOutputType } from '@amzn/awscat-editor';
import FlashbarMessageBroker from '../common/FlashbarMessageBroker';
import { transformTiptapHtml } from '../api/ContentUtil';
import { withAppContext } from '../contexts/AppContext';
import AppContextProps from '../contexts/AppContextProps';
import {
  ContentViewerContextData,
  withContentViewerContext,
} from '../components/workflow-viewer/ContentViewerContext';
import {
  InlineComment,
  ContentType,
  UserCommentState,
} from '@amzn/awscat-pattern-library-service-model';
import { getUserRoles } from '../api/UtilsApi';
import { getPathTitle, sanitizeCodeBlock, fetchImageInBase64Format } from './utils';
import ErrorBoundary from './ErrorBoundary';
import ImageAltEntitiesModal from '../components/content/ImageAltEntitiesModal';
import SubsectionIdModal from '../components/content/SubsectionIdModal';
import { resolveAltEntitiesValue } from './resolveAltEntitiesValue';

export interface EditorInputProps extends AppContextProps {
  contentId: string;
  data: string;
  sectionId: string;
  readonly: boolean;
  onContentChanged?: (
    sectionId: string,
    key: ContentSectionKey,
    newContent: TiptapOutputType,
  ) => void;
  contentViewerContext: ContentViewerContextData;
  content: any;
}

const ALLOWED_VIDEO_SOURCES = ['youtube.com/watch'];

interface EditorInputState {
  richTextData: TiptapOutputType;
  prevContentId: string;
  prevSectionId: string;
  unallowedVideoInnerHTML?: string;
  isAltEntitiesModalVisible: boolean;
  altEntitiesValue: TiptapOutputType;
  altEntitiesEditor: any;
  altEntitiesNodePos: number;
  isSubsectionIdModalVisible: boolean;
  subsectionIdValue: string;
  subsectionIdEditor: any;
  subsectionIdNodeId: string | null;
}

class EditorInput extends React.Component<EditorInputProps, EditorInputState> {
  static defaultProps = {};
  private readonly mplService: IMplServiceClient;
  private flashbarMessageBroker: FlashbarMessageBroker;
  private tiptapMenuItems: any[];

  constructor(props: EditorInputProps) {
    super(props);
    this.mplService = MplServiceClientFactory.create();
    this.flashbarMessageBroker = FlashbarMessageBroker.create();

    this.tiptapMenuItems = [
      new Undo(),
      new Redo(),
      new Newline(),
      new BlockFormatGroup(),
      new Newline(),
      new Bold(),
      new Italic(),
      new Underline(),
      new Code(),
      new Newline(),
      new TextAlignLeft(),
      new TextAlignCenter(),
      new TextAlignRight(),
      new Newline(),
      new OrderedList(),
      new BulletList(),
      new Link({
        beforeInsert: this.validateInsertedLink,
        validateLink: (link) => /(^https:\/\/)|(^www\.)/.test(link),
      }),
      new Newline(),
      new Image({ imageUpload: this.uploadImage }),
      new Youtube({ beforeInsert: this.isVideoAllowed }),
      new Table(),
      new Newline(),
      new ClearFormatting(),
    ];

    // Enable Codeview editing for Admin/Techwriter users.
    const userProfile = this.props.appContext.authnInfo?.userProfile;
    if (userProfile) {
      const { isAdmin, isTechWriter } = getUserRoles(userProfile);
      if (isAdmin || isTechWriter) {
        this.tiptapMenuItems.push(new CodeView());
      }
    }
    this.tiptapMenuItems.push(new SearchAndReplace());

    const { data } = props;
    this.state = {
      richTextData:
        typeof data === 'string' && !this.props.readonly ? transformTiptapHtml(data) : data,
      prevContentId: props.contentId,
      prevSectionId: props.sectionId,
      unallowedVideoInnerHTML: '',
      isAltEntitiesModalVisible: false,
      altEntitiesValue: '',
      altEntitiesEditor: null,
      altEntitiesNodePos: 0,
      isSubsectionIdModalVisible: false,
      subsectionIdValue: '',
      subsectionIdEditor: null,
      subsectionIdNodeId: null,
    };
  }

  public componentDidUpdate(prevProps: EditorInputProps) {
    // A section switch is already handled before render by getDerivedStateFromProps.
    if (
      prevProps.data !== this.props.data &&
      prevProps.contentId === this.props.contentId &&
      prevProps.sectionId === this.props.sectionId
    ) {
      const { data } = this.props;
      this.setState({
        richTextData:
          typeof data === 'string' && !this.props.readonly ? transformTiptapHtml(data) : data,
      });
    }
  }

  // Swap in the new section's data before the render that shows it, so the editor never
  // renders the previous section's content under the new section.
  public static getDerivedStateFromProps(
    props: EditorInputProps,
    state: EditorInputState,
  ): Partial<EditorInputState> | null {
    if (props.contentId === state.prevContentId && props.sectionId === state.prevSectionId) {
      return null;
    }
    return {
      richTextData:
        typeof props.data === 'string' && !props.readonly
          ? transformTiptapHtml(props.data)
          : props.data,
      prevContentId: props.contentId,
      prevSectionId: props.sectionId,
    };
  }

  public shouldComponentUpdate(nextProps: any, nextState: any) {
    const contentChanged = this.props.contentId !== nextProps.contentId;
    const sectionChanged = this.props.sectionId !== nextProps.sectionId;
    const modalStateChanged =
      this.state.isAltEntitiesModalVisible !== nextState.isAltEntitiesModalVisible ||
      this.state.isSubsectionIdModalVisible !== nextState.isSubsectionIdModalVisible;
    if (contentChanged || sectionChanged || modalStateChanged) {
      return true;
    }
    const { readonly } = nextProps;
    if (
      readonly &&
      this.props?.contentViewerContext.isCommentingModeEnabled !==
        nextProps?.contentViewerContext.isCommentingModeEnabled
    ) {
      return true;
    }
    const nextActiveComments = nextProps?.contentViewerContext?.inlineComments;
    const prevActiveComments = this.props?.contentViewerContext?.inlineComments;
    if (
      this.filterSectionComments(prevActiveComments || []).length !==
      this.filterSectionComments(nextActiveComments || []).length
    ) {
      return true;
    }
    // when richtext data changed in readonly mode for viewing changes in bar raiser view
    const shouldUpdate =
      this.state.richTextData !== nextState.richTextData || this.props.data !== nextProps.data;
    if (readonly && shouldUpdate) {
      return true;
    }
    return false;
  }

  public filterSectionComments(inlineComments: InlineComment[]) {
    const { sectionId } = this.props;
    const contentType = this.props?.contentViewerContext?.content?.contentMetadata?.contentType;
    let sectionComments: InlineComment[] = [];
    if (contentType === ContentType.Pattern) {
      sectionComments = inlineComments.filter((comment) => comment.path[0] === sectionId);
      // @ts-expect-error TS2345 - strict migration
    } else if ([ContentType.Guide, ContentType.Strategy].includes(contentType)) {
      sectionComments = inlineComments.filter(
        (comment) =>
          comment.path[0] === getPathTitle(this.props?.contentViewerContext?.currentPage?.title) &&
          comment.path[1] === this.props?.content?.id,
      );
    }
    return sectionComments.filter((comment) => comment.state !== UserCommentState.RESOLVED);
  }

  public render() {
    const { readonly } = this.props;
    const { richTextData } = this.state;
    const { isCommentingModeEnabled } = this.props?.contentViewerContext ?? {};
    const { entities } = this.props?.appContext ?? {};
    const disableCommentingMode = !isCommentingModeEnabled;
    const sectionComments = this.filterSectionComments(
      this.props?.contentViewerContext?.inlineComments ?? [],
    );

    const tiptapEditor = (
      <ErrorBoundary
        fallback={
          <TiptapEditor
            config={{ stickyHeader: true }}
            content={richTextData}
            menuItems={this.tiptapMenuItems}
            onChange={this.changeEditorState}
            outputFormats={['json', 'html']}
            comments={[]}
            // @ts-expect-error TS2322 - strict migration
            updateInlineCommentsLocation={this.updateCommentLocation}
            // @ts-expect-error TS2322 - strict migration
            entities={entities}
            fetchImage={this.fetchImage}
            editable
            enableUniqueId={true}
            onEditAltEntities={this.handleEditAltEntities}
            onEditSubsectionId={this.handleEditSubsectionId}
            onCopySubsectionAnchor={this.handleCopySubsectionAnchor}
          />
        }
      >
        <TiptapEditor
          config={{ stickyHeader: true }}
          content={richTextData}
          menuItems={this.tiptapMenuItems}
          onChange={this.changeEditorState}
          outputFormats={['json', 'html']}
          comments={sectionComments}
          // @ts-expect-error TS2322 - strict migration
          updateInlineCommentsLocation={this.updateCommentLocation}
          // @ts-expect-error TS2322 - strict migration
          entities={entities}
          imageUpload={this.uploadImage}
          fetchImage={this.fetchImage}
          editable
          enableUniqueId={true}
          onEditAltEntities={this.handleEditAltEntities}
          onEditSubsectionId={this.handleEditSubsectionId}
          onCopySubsectionAnchor={this.handleCopySubsectionAnchor}
        />
      </ErrorBoundary>
    );

    const tiptapReadOnlyView = (
      <ErrorBoundary
        fallback={
          // @ts-expect-error TS2322 - strict migration
          <TiptapEditorView model={richTextData} entities={entities} fetchImage={this.fetchImage} />
        }
      >
        {/* @ts-expect-error TS2322 - strict migration */}
        <TiptapEditorView model={richTextData} entities={entities} fetchImage={this.fetchImage} />
      </ErrorBoundary>
    );

    const readonlyEditor =
      typeof richTextData === 'string' ? (
        <TiptapEditorHTMLView model={richTextData} />
      ) : disableCommentingMode ? (
        tiptapReadOnlyView
      ) : (
        <ErrorBoundary
          fallback={
            <TiptapEditorCommentView
              model={richTextData}
              addInlineComment={this.createInlinComment}
              comments={[]}
              disableCommentingMode={disableCommentingMode}
              // @ts-expect-error TS2322 - strict migration
              entities={entities}
              fetchImage={this.fetchImage}
            />
          }
        >
          <TiptapEditorCommentView
            model={richTextData}
            addInlineComment={this.createInlinComment}
            comments={sectionComments}
            disableCommentingMode={disableCommentingMode}
            // @ts-expect-error TS2322 - strict migration
            entities={entities}
            fetchImage={this.fetchImage}
          />
        </ErrorBoundary>
      );

    return (
      <Box variant="div" padding={{ top: 's' }}>
        <div className="editorbox-small">{readonly ? readonlyEditor : tiptapEditor}</div>
        <ImageAltEntitiesModal
          visible={this.state.isAltEntitiesModalVisible}
          onDismiss={this.dismissAltEntitiesModal}
          onSave={this.saveAltEntities}
          initialValue={this.state.altEntitiesValue}
        />
        <SubsectionIdModal
          visible={this.state.isSubsectionIdModalVisible}
          onDismiss={this.dismissSubsectionIdModal}
          onSave={this.saveSubsectionId}
          initialValue={this.state.subsectionIdValue}
        />
      </Box>
    );
  }

  private createInlinComment = async (
    from: number,
    to: number,
    textData: string,
    commentData: string,
  ) => {
    const { sectionId } = this.props;
    let path = [sectionId];
    const contentType = this.props?.contentViewerContext?.content?.contentMetadata?.contentType;
    // @ts-expect-error TS2345 - strict migration
    if ([ContentType.Guide, ContentType.Strategy].includes(contentType)) {
      path = [
        getPathTitle(this.props?.contentViewerContext?.currentPage?.title),
        this.props?.content?.id,
      ];
    }

    // @ts-expect-error TS2722 - strict migration
    await this.props?.contentViewerContext?.createInlineComment(
      path,
      from,
      to,
      textData,
      commentData,
    );
  };

  private updateCommentLocation = (comments: InlineComment[]) => {
    if (!comments.length) {
      return;
    }
    const contentType = this.props?.contentViewerContext?.content?.contentMetadata?.contentType;
    let { sectionId } = this.props;
    // @ts-expect-error TS2345 - strict migration
    if ([ContentType.Guide, ContentType.Strategy].includes(contentType)) {
      // @ts-expect-error TS2532 - strict migration
      sectionId = comments[0].path[0] + comments[0].path?.[1];
    }
    // @ts-expect-error TS2722 - strict migration
    this.props?.contentViewerContext?.updateInlineCommentsLocation(comments, sectionId);
  };

  private changeEditorState = (editor: any, output: TiptapOutput) => {
    const sanitizedCodeBlock = sanitizeCodeBlock(output.json);
    this.setState({ richTextData: output.json });
    const { sectionId, onContentChanged } = this.props;
    if (onContentChanged) {
      onContentChanged(sectionId, ContentSectionKey.ContentData, sanitizedCodeBlock);
    }
  };

  private uploadImage = async (image: File) => {
    const { contentId } = this.props;
    if (image instanceof File) {
      const response = await this.mplService.putContentImage(contentId, image);
      return response.data.link;
    }
  };

  private fetchImage = async (srcUrl: string) => {
    return await fetchImageInBase64Format(srcUrl, this.mplService);
  };

  // validate video
  private isVideoAllowed = async (video: string) => {
    if (!video) {
      return false;
    }
    if (
      !ALLOWED_VIDEO_SOURCES.some((allowedVideoSource) =>
        video.toLowerCase().includes(allowedVideoSource),
      )
    ) {
      this.flashbarMessageBroker.pushErrorMessage(
        'Could not insert Youtube video. Please provide urls for youtube videos in the format of youtube.com/watch?v=...',
      );
      return false;
    }
    return true;
  };

  // Validate link before insert
  private validateInsertedLink = async (link: string) => {
    if (link.startsWith('/') || link.startsWith('#')) {
      return true;
    }

    // Validate link to enforce valid URL format and HTTPS
    if (!isURL(link) || !link.toLowerCase().startsWith('https://')) {
      this.flashbarMessageBroker.pushErrorMessage(
        'Could not insert link. Your URL is invalid or not using HTTPS.',
      );
      return false;
    }
    return true;
  };

  private handleEditAltEntities = (editor: any) => {
    const { from } = editor.state.selection;
    const node = editor.state.doc.nodeAt(from);
    const currentValue = node?.attrs?.['data-alt-entities'];
    const altValue = node?.attrs?.alt;
    const parsedValue = resolveAltEntitiesValue(currentValue, altValue);
    this.setState({
      isAltEntitiesModalVisible: true,
      altEntitiesValue: parsedValue,
      altEntitiesEditor: editor,
      altEntitiesNodePos: from,
    });
  };

  private dismissAltEntitiesModal = () => {
    this.setState({ isAltEntitiesModalVisible: false, altEntitiesEditor: null });
  };

  private saveAltEntities = (altEntities: any) => {
    const { altEntitiesEditor, altEntitiesNodePos } = this.state;
    if (altEntitiesEditor) {
      const valueToStore =
        altEntities && typeof altEntities === 'object'
          ? JSON.stringify(altEntities)
          : altEntities || null;

      // Count which image (by document order) is at the stored position
      let imageIndex = 0;
      altEntitiesEditor.state.doc.descendants((node: any, pos: number) => {
        if (node.type.name === 'image' && pos < altEntitiesNodePos) {
          imageIndex++;
        }
      });

      // Patch the matching image in the JSON by index
      const json = altEntitiesEditor.getJSON();
      let currentIndex = 0;
      const patchImageNode = (docNode: any): boolean => {
        if (docNode?.type === 'image') {
          if (currentIndex === imageIndex) {
            docNode.attrs = { ...docNode.attrs, 'data-alt-entities': valueToStore };
            return true;
          }
          currentIndex++;
        }
        if (docNode?.content) {
          for (const child of docNode.content) {
            if (patchImageNode(child)) return true;
          }
        }
        return false;
      };
      patchImageNode(json);

      // Propagate the patched JSON to the parent
      this.setState({ richTextData: json });
      const { sectionId, onContentChanged } = this.props;
      if (onContentChanged) {
        onContentChanged(sectionId, ContentSectionKey.ContentData, json);
      }
    }
    this.setState({ isAltEntitiesModalVisible: false, altEntitiesEditor: null });
  };

  private handleEditSubsectionId = (editor: any) => {
    const { from } = editor.state.selection;
    // Walk up from the cursor to find the heading node
    const $pos = editor.state.doc.resolve(from);
    let headingNode = null;
    let headingNodeId = null;
    for (let depth = $pos.depth; depth >= 0; depth--) {
      const node = $pos.node(depth);
      if (node.type.name === 'heading') {
        headingNode = node;
        headingNodeId = node.attrs?.nodeId || null;
        break;
      }
    }
    // Fallback: check if the node at cursor is a heading
    if (!headingNode) {
      const nodeAt = editor.state.doc.nodeAt(from);
      if (nodeAt?.type?.name === 'heading') {
        headingNode = nodeAt;
        headingNodeId = nodeAt.attrs?.nodeId || null;
      }
    }
    const currentId = headingNode?.attrs?.id || '';
    this.setState({
      isSubsectionIdModalVisible: true,
      subsectionIdValue: currentId,
      subsectionIdEditor: editor,
      subsectionIdNodeId: headingNodeId,
    });
  };

  private handleCopySubsectionAnchor = (editor: any) => {
    const { from } = editor.state.selection;
    const $pos = editor.state.doc.resolve(from);
    let headingNode = null;
    for (let depth = $pos.depth; depth >= 0; depth--) {
      const node = $pos.node(depth);
      if (node.type.name === 'heading') {
        headingNode = node;
        break;
      }
    }
    if (!headingNode) {
      const nodeAt = editor.state.doc.nodeAt(from);
      if (nodeAt?.type?.name === 'heading') {
        headingNode = nodeAt;
      }
    }
    const id = headingNode?.attrs?.id;
    if (id) {
      navigator.clipboard.writeText(`#${id}`);
    }
  };

  private dismissSubsectionIdModal = () => {
    this.setState({ isSubsectionIdModalVisible: false, subsectionIdEditor: null });
  };

  private saveSubsectionId = (subsectionId: string) => {
    const { subsectionIdEditor, subsectionIdNodeId } = this.state;
    if (subsectionIdEditor && subsectionIdNodeId) {
      const valueToStore = subsectionId || null;

      // Patch the heading node in JSON by nodeId
      const json = subsectionIdEditor.getJSON();
      const patchHeadingNode = (docNode: any): boolean => {
        if (docNode?.type === 'heading' && docNode?.attrs?.nodeId === subsectionIdNodeId) {
          docNode.attrs = { ...docNode.attrs, id: valueToStore };
          return true;
        }
        if (docNode?.content) {
          for (const child of docNode.content) {
            if (patchHeadingNode(child)) return true;
          }
        }
        return false;
      };
      patchHeadingNode(json);

      // Propagate the patched JSON to the parent
      this.setState({ richTextData: json });
      const { sectionId, onContentChanged } = this.props;
      if (onContentChanged) {
        onContentChanged(sectionId, ContentSectionKey.ContentData, json);
      }
    }
    this.setState({ isSubsectionIdModalVisible: false, subsectionIdEditor: null });
  };
}

const WrappedEditorInput = withContentViewerContext(withAppContext(EditorInput));
export default WrappedEditorInput;
